import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight, CheckCircle, ExternalLink } from "lucide-react";
import * as Icons from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import { MODULES, MODULE_COLOR } from "./moduleData";
import "../../marketing.css";

export default function MarketingModule() {
  const { slug } = useParams();
  const mod = MODULES.find(m => m.slug === slug);
  if (!mod) return <Navigate to="/marketing" replace />;

  const c = MODULE_COLOR[mod.color] || MODULE_COLOR.orange;
  const Icon = Icons[mod.icon] || Icons.Plane;
  const others = MODULES.filter(m => m.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── PROBLEM HERO ───────────────────────────────────────── */}
      <section className="pt-28 pb-16 relative overflow-hidden grid-texture">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 50% at 30% 50%, rgba(239,68,68,0.05) 0%, transparent 70%)" }} />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest">
              <Link to="/marketing" className="hover:text-white transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/marketing/platform" className="hover:text-white transition-colors">Platform</Link>
              <span className="mx-2">/</span>
              {mod.name}
            </span>
          </div>

          <div className={`inline-flex items-center gap-2 ${c.bg} border ${c.border} px-3 py-1.5 rounded mb-5`}>
            <span className={`text-[10px] marketing-font-mono uppercase font-bold tracking-widest ${c.text}`}>{mod.badge}</span>
          </div>

          <h1 className="text-4xl lg:text-5xl font-black marketing-font-heading leading-tight mb-4">
            {mod.name}
          </h1>
          <p className={`text-xl font-semibold marketing-font-heading mb-8 ${c.text}`}>"{mod.tagline}"</p>

          {/* Problem box */}
          <div className="bg-red-500/5 border-l-4 border-red-500/60 pl-6 pr-6 py-5 rounded-r-xl mb-8">
            <div className="text-[9px] text-red-400 marketing-font-mono uppercase tracking-widest font-bold mb-2">The Problem</div>
            <p className="text-slate-300 leading-relaxed">{mod.problem}</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/marketing/demo"
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5">
              Request Demo <ArrowRight size={16} />
            </Link>
            <Link to={mod.appLink}
              className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 hover:text-white px-7 py-3.5 transition-all">
              <ExternalLink size={14} /> See it live
            </Link>
          </div>
        </div>
      </section>

      {/* ── SOLUTION ───────────────────────────────────────────── */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`text-[10px] marketing-font-mono uppercase tracking-widest font-bold mb-3 ${c.text}`}>The Solution</div>
              <h2 className="text-3xl font-black marketing-font-heading mb-5">{mod.name}</h2>
              <p className="text-slate-400 leading-relaxed text-base">{mod.solution}</p>
            </div>
            <div className={`bg-[#0c1929]/60 border ${c.border} rounded-xl p-6`}>
              <div className={`text-[9px] marketing-font-mono uppercase tracking-widest font-bold mb-4 ${c.text}`}>Sub-Platform</div>
              <div className="flex items-center gap-3 mb-4">
                <Icon size={24} className={c.text} />
                <div>
                  <div className="font-bold text-white marketing-font-heading">{mod.name}</div>
                  <div className={`text-[10px] marketing-font-mono ${c.text}`}>{mod.brand}</div>
                </div>
              </div>
              <div className="space-y-2">
                {mod.steps.map((s, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle size={13} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-400">{s.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ───────────────────────────────────────── */}
      <section className="py-16 border-t border-white/5 grid-texture">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className={`text-[10px] marketing-font-mono uppercase tracking-widest font-bold mb-3 ${c.text}`}>How It Works</div>
          <h2 className="text-3xl font-black marketing-font-heading mb-10">Three steps to full control</h2>

          <div className="grid md:grid-cols-3 gap-6">
            {mod.steps.map((s, i) => (
              <div key={i} className={`bg-[#0c1929]/60 border ${c.border} p-6 card-glow fade-in-up fade-in-up-${i + 1}`}>
                <div className={`text-4xl font-black marketing-font-mono mb-4 ${c.text} opacity-40`}>{s.n}</div>
                <h3 className="font-bold text-white marketing-font-heading mb-3">{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MOCK UI ────────────────────────────────────────────── */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className={`text-[10px] marketing-font-mono uppercase tracking-widest font-bold mb-3 ${c.text}`}>Live Platform</div>
          <h2 className="text-3xl font-black marketing-font-heading mb-3">See it running now</h2>
          <p className="text-slate-500 mb-8 text-sm">The live application is running with real demo data. Click below to explore.</p>

          <Link to={mod.appLink}
            className={`group block bg-[#0c1929]/60 border ${c.border} rounded-xl overflow-hidden hover:border-opacity-60 transition-all card-glow`}>
            <div className={`flex items-center justify-between px-5 py-3 border-b border-white/5 ${c.bg}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full live-dot ${c.text.replace("text-", "bg-")}`} />
                <span className={`text-[10px] marketing-font-mono uppercase font-bold ${c.text}`}>{mod.name} · LIVE DEMO</span>
              </div>
              <ExternalLink size={13} className="text-slate-600 group-hover:text-white transition-colors" />
            </div>
            <div className="px-5 py-10 text-center">
              <Icon size={40} className={`${c.text} mx-auto mb-4 opacity-40`} />
              <p className="text-slate-500 text-sm mb-4">Click to open {mod.name} in the live demo platform</p>
              <span className={`inline-flex items-center gap-2 ${c.bg} border ${c.border} ${c.text} px-5 py-2.5 text-sm font-bold transition-all group-hover:shadow-lg`}>
                Open Live Demo <ChevronRight size={14} />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* ── ROI ────────────────────────────────────────────────── */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className={`text-[10px] marketing-font-mono uppercase tracking-widest font-bold mb-3 ${c.text}`}>The Outcome</div>
          <h2 className="text-3xl font-black marketing-font-heading mb-10">What you get on day one</h2>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {mod.roi.map((r, i) => (
              <div key={i} className="bg-[#0c1929]/60 border border-white/5 p-6 text-center card-glow fade-in-up">
                <div className="text-4xl font-black marketing-font-mono text-orange-400 mb-2">{r.metric}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-widest marketing-font-mono">{r.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            <Link to="/marketing/demo"
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5">
              Get a demo for your airline <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── OTHER MODULES ──────────────────────────────────────── */}
      <section className="py-16 border-t border-white/5 bg-[#04090f]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest font-bold mb-6">Other Modules</div>
          <div className="grid md:grid-cols-3 gap-4">
            {others.map(m => {
              const oc = MODULE_COLOR[m.color];
              return (
                <Link key={m.slug} to={`/marketing/module/${m.slug}`}
                  className={`bg-[#0c1929]/60 border ${oc.border} p-5 card-glow hover:shadow-lg transition-all group`}>
                  <div className={`text-[9px] marketing-font-mono uppercase font-bold mb-2 ${oc.text}`}>{m.badge}</div>
                  <div className="font-bold text-white text-sm group-hover:text-orange-100 transition-colors">{m.name}</div>
                  <div className="text-[11px] text-slate-600 mt-1.5 line-clamp-2">{m.tagline}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
